import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Skeleton } from 'primereact/skeleton';
import { Tag } from 'primereact/tag';

export const FeedbackStatesSection = () => {
  return (
    <div id='feedback-states' className='space-y-6'>
      <div>
        <h2 className='text-2xl font-bold mb-2'>Feedback & States</h2>
        <p className='text-gray-600 mb-4'>
          Components for loading states, empty states, and status feedback.
        </p>
      </div>

      {/* Progress Spinner */}
      <Card title='Progress Spinner' className='mb-4'>
        <div className='space-y-6'>
          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Default Spinner
            </h3>
            <div className='flex items-center gap-6'>
              <ProgressSpinner style={{ width: '50px', height: '50px' }} />
              <ProgressSpinner style={{ width: '30px', height: '30px' }} />
              <ProgressSpinner style={{ width: '20px', height: '20px' }} />
            </div>
          </div>

          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Custom Colors
            </h3>
            <div className='flex items-center gap-6'>
              <ProgressSpinner
                style={{ width: '50px', height: '50px' }}
                strokeWidth='4'
                fill='transparent'
                animationDuration='1s'
              />
              <ProgressSpinner
                style={{ width: '50px', height: '50px' }}
                strokeWidth='8'
                fill='transparent'
                animationDuration='2s'
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Loading States */}
      <Card title='Loading States with Skeleton' className='mb-4'>
        <div className='space-y-6'>
          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Card Loading
            </h3>
            <div className='border rounded-lg p-4 space-y-4'>
              <div className='flex items-center gap-3'>
                <Skeleton shape='circle' size='3rem' />
                <div className='flex-1 space-y-2'>
                  <Skeleton width='40%' height='1.25rem' />
                  <Skeleton width='60%' height='1rem' />
                </div>
              </div>
              <Skeleton width='100%' height='6rem' />
              <div className='flex gap-2'>
                <Skeleton width='6rem' height='2.5rem' />
                <Skeleton width='6rem' height='2.5rem' />
              </div>
            </div>
          </div>

          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Table Loading
            </h3>
            <div className='border rounded-lg p-4 space-y-3'>
              <Skeleton width='100%' height='2rem' />
              <Skeleton width='100%' height='1.5rem' />
              <Skeleton width='100%' height='1.5rem' />
              <Skeleton width='100%' height='1.5rem' />
              <Skeleton width='100%' height='1.5rem' />
            </div>
          </div>

          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Form Loading
            </h3>
            <div className='space-y-4'>
              <div>
                <Skeleton width='8rem' height='1rem' className='mb-2' />
                <Skeleton width='100%' height='2.5rem' />
              </div>
              <div>
                <Skeleton width='10rem' height='1rem' className='mb-2' />
                <Skeleton width='100%' height='2.5rem' />
              </div>
              <div className='flex gap-2'>
                <Skeleton width='6rem' height='2.5rem' />
                <Skeleton width='6rem' height='2.5rem' />
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Empty States */}
      <Card title='Empty States' className='mb-4'>
        <div className='space-y-6'>
          <div className='bg-gray-50 rounded-lg p-8 text-center'>
            <i className='pi pi-inbox text-5xl text-gray-400 mb-4'></i>
            <h3 className='text-lg font-semibold text-gray-700 mb-2'>
              No Data Available
            </h3>
            <p className='text-gray-500 mb-4'>
              There are no items to display at this time.
            </p>
            <Button label='Add New Item' icon='pi pi-plus' />
          </div>

          <div className='bg-gray-50 rounded-lg p-8 text-center'>
            <i className='pi pi-search text-5xl text-gray-400 mb-4'></i>
            <h3 className='text-lg font-semibold text-gray-700 mb-2'>
              No Results Found
            </h3>
            <p className='text-gray-500 mb-4'>
              Try adjusting your search or filter criteria.
            </p>
            <Button label='Clear Filters' icon='pi pi-filter-slash' outlined />
          </div>

          <div className='bg-gray-50 rounded-lg p-8 text-center'>
            <i className='pi pi-folder-open text-5xl text-gray-400 mb-4'></i>
            <h3 className='text-lg font-semibold text-gray-700 mb-2'>
              No Projects Yet
            </h3>
            <p className='text-gray-500 mb-4'>
              Get started by creating your first project.
            </p>
            <Button
              label='Create Project'
              icon='pi pi-plus'
              severity='success'
            />
          </div>
        </div>
      </Card>

      {/* Error States */}
      <Card title='Error States' className='mb-4'>
        <div className='space-y-4'>
          <div className='bg-red-50 border border-red-200 rounded-lg p-6 text-center'>
            <i className='pi pi-exclamation-circle text-4xl text-red-500 mb-3'></i>
            <h3 className='text-lg font-semibold text-red-900 mb-2'>
              Error Loading Data
            </h3>
            <p className='text-red-700 mb-4'>
              Unable to fetch the requested information. Please try again.
            </p>
            <Button label='Retry' icon='pi pi-refresh' severity='danger' />
          </div>

          <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-6'>
            <div className='flex items-start gap-3'>
              <i className='pi pi-exclamation-triangle text-2xl text-yellow-600'></i>
              <div className='flex-1'>
                <h3 className='text-lg font-semibold text-yellow-900 mb-1'>
                  Connection Issues
                </h3>
                <p className='text-yellow-700 mb-3'>
                  We&apos;re having trouble connecting to the server. Some
                  features may be unavailable.
                </p>
                <div className='flex gap-2'>
                  <Button
                    label='Retry Connection'
                    icon='pi pi-refresh'
                    severity='warning'
                    size='small'
                  />
                  <Button
                    label='Work Offline'
                    severity='warning'
                    size='small'
                    outlined
                  />
                </div>
              </div>
            </div>
          </div>

          <Message
            severity='error'
            text='Failed to save changes. Please check your connection and try again.'
          />
        </div>
      </Card>

      {/* Status Badges */}
      <Card title='Status Badges' className='mb-4'>
        <div className='space-y-6'>
          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Survey Status
            </h3>
            <div className='flex flex-wrap gap-3'>
              <div className='flex items-center gap-2 px-4 py-2 bg-green-50 rounded-lg'>
                <Tag value='Published' severity='success' />
                <span className='text-sm text-gray-600'>
                  Live and accepting responses
                </span>
              </div>
              <div className='flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg'>
                <Tag value='Draft' severity='info' />
                <span className='text-sm text-gray-600'>Work in progress</span>
              </div>
              <div className='flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-lg'>
                <Tag value='Scheduled' severity='warning' />
                <span className='text-sm text-gray-600'>
                  Scheduled for future
                </span>
              </div>
              <div className='flex items-center gap-2 px-4 py-2 bg-red-50 rounded-lg'>
                <Tag value='Completed' severity='danger' />
                <span className='text-sm text-gray-600'>
                  No longer accepting
                </span>
              </div>
              <div className='flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg'>
                <Tag value='Archived' />
                <span className='text-sm text-gray-600'>Moved to archive</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              User Status
            </h3>
            <div className='flex flex-wrap gap-3'>
              <div className='flex items-center gap-2'>
                <div className='w-2 h-2 bg-green-500 rounded-full'></div>
                <Tag value='Active' severity='success' rounded />
              </div>
              <div className='flex items-center gap-2'>
                <div className='w-2 h-2 bg-yellow-500 rounded-full'></div>
                <Tag value='Away' severity='warning' rounded />
              </div>
              <div className='flex items-center gap-2'>
                <div className='w-2 h-2 bg-red-500 rounded-full'></div>
                <Tag value='Offline' severity='danger' rounded />
              </div>
            </div>
          </div>

          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Priority Badges
            </h3>
            <div className='flex flex-wrap gap-3'>
              <Tag
                value='High Priority'
                severity='danger'
                icon='pi pi-exclamation-circle'
              />
              <Tag
                value='Medium Priority'
                severity='warning'
                icon='pi pi-info-circle'
              />
              <Tag
                value='Low Priority'
                severity='info'
                icon='pi pi-minus-circle'
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Success States */}
      <Card title='Success States' className='mb-4'>
        <div className='bg-green-50 border border-green-200 rounded-lg p-8 text-center'>
          <i className='pi pi-check-circle text-5xl text-green-500 mb-4'></i>
          <h3 className='text-lg font-semibold text-green-900 mb-2'>
            Successfully Saved!
          </h3>
          <p className='text-green-700 mb-4'>
            Your changes have been saved successfully.
          </p>
          <Button
            label='Continue'
            icon='pi pi-arrow-right'
            iconPos='right'
            severity='success'
          />
        </div>
      </Card>
    </div>
  );
};
